import { LocalizationString } from '@celito.clients/assets';
import {
  ActionTypeEnum,
  AttributeTypeEnum,
  ControlledDocumentObjectAttributeNameEnum,
  DocumentDownloadActionEnum,
  DocumentSubTypeObjectAttributeNameEnum,
  DocumentTypeObjectColumnNameEnum,
  OperatorsEnum,
} from '@celito.clients/enums';
import {
  Field,
  LayoutRuleConditionEnum,
  ObjectActionDefinition,
  ObjectAttributeDefinition,
  ObjectAttributeType,
  Section,
} from '@celito.clients/types';
import { errorToast, infoToast, raiseErrorToast } from '@celito.clients/utils';

import { FieldEnum } from './config/field-enum';
import { getDocumentData } from './services';
import { ControlledDocs, DocumentFile, UserActions } from './types';

const EmptyArray: Array<any> = [];
const EmptyString = '';

export const userType = {
  Collaborator: 'cd_create_collaboration',
  Owner: 'cd_create_complete_draft',
  Admin: ['cd_create_admin_review', 'cd_pre_approved_doc_admin_review'],
  Functional_Approval: 'cd_create_functional_approval',
  Quality_Approval: 'cd_create_quality_approval',
};

export const nonCollaborativeFiles = ['.jpeg', '.jpg', '.png', '.pdf'];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TODO = any;

export const getLifeCycleStageGroups = (
  attributeObject: ObjectAttributeType,
  currentLifeCycleStage: string
) => {
  const lifecycleStageGroups = attributeObject?.lifecycleStageGroups;
  if (lifecycleStageGroups) {
    const lifecycleStages: string[] = [];
    const lifeCycleStageGroupEntries = Object.entries(lifecycleStageGroups);

    for (const lifeCycleStageGroupEntry of lifeCycleStageGroupEntries.sort(
      (a, b) => a[1].displayOrder - b[1].displayOrder
    )) {
      const lifeCycleStageGroup = lifeCycleStageGroupEntry[1];
      const stageName = lifeCycleStageGroupEntry[0];

      if (lifeCycleStageGroup.displayActively) {
        lifecycleStages.push(stageName);
      } else {
        // if the displayActively is false check that the current lifecycle state is present in the lifecycleStageGroups, if yes then show in the chevron
        if (lifeCycleStageGroup?.stages?.includes(currentLifeCycleStage)) {
          lifecycleStages.push(stageName);
        }
      }
    }
    return lifecycleStages;
  }
};

export const getActiveLifeCycleStage = (
  attributeObject: ObjectAttributeType,
  response: ControlledDocs
) => {
  const wizardStates = attributeObject?.lifecycleStageGroups;
  for (const wizard in wizardStates) {
    if (wizardStates[wizard]?.stages.includes(response?.lifecycleStage)) {
      return wizard;
    }
  }
};

const ActionTypesThatRequireTodo = [
  ActionTypeEnum.DELEGATE_TASK,
  ActionTypeEnum.EDIT_DOCUMENT,
];

export const getUserActions = (
  useractions: UserActions[],
  listOfActions: ObjectActionDefinition[],
  userHasTodoTask: boolean
) => {
  const filterActions: ObjectActionDefinition[] = [];
  useractions.map((useraction: UserActions) => {
    listOfActions.map((actions: ObjectActionDefinition) => {
      if (
        actions.name === useraction.name &&
        // Tasks that require to do should not be shown if user does'nt have todo
        (userHasTodoTask ||
          !ActionTypesThatRequireTodo.includes(actions.actionType))
      ) {
        actions.permission = useraction.permission;
        // Removing the edit document from context menu
        if (actions.actionType !== ActionTypeEnum.EDIT_DOCUMENT) {
          filterActions.push(actions);
        }
      }
    });
  });
  return filterActions;
};

export const handleDownload = (documentData: DocumentFile) => {
  try {
    if (documentData?.fileBuffer) {
      const uint8Array = new Uint8Array(documentData?.fileBuffer?.data);
      const blob = new Blob([uint8Array], {
        type: documentData?.mimeType,
      });
      const downloadLink = document?.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = documentData?.label;
      downloadLink.click();
    }
  } catch (error) {
    errorToast({ message: `${error}` });
  } finally {
    // setLoading(false);
  }
};

export const getDocumentFile = (
  id: string,
  name: string,
  recordversion?: string,
  version?: string
) => {
  infoToast({
    message: LocalizationString.DOWNLOADING_MESSAGE,
  });
  getDocumentData(id, name, version, DocumentDownloadActionEnum.DownloadRaw)
    .then((documentDocs: DocumentFile) => {
      if (documentDocs) {
        if (documentDocs?.documentId) {
          handleDownload(documentDocs);
        }
      }
    })
    .catch((_error) => {
      raiseErrorToast(_error);
    });
};

export const getUserList = (list: TODO[]) => {
  const users: string[] = [];
  for (const user of list || []) {
    users.push(user.name);
  }
  return users || [];
};

export const mergeSections = (sectionData: Partial<Section>[] | undefined) => {
  let mergedFields: Field[] = [];
  for (const section of sectionData || []) {
    const fields = section.fields || [];

    mergedFields = mergedFields.concat(fields);
  }
  return { fields: mergedFields };
};

const getDefaultValuesByAttributeDataType = (
  attribute: ObjectAttributeDefinition
) => {
  // First check if attribute has a value from backend
  if (attribute.value !== undefined && attribute.value !== null) {
    return attribute.value;
  }

  // Otherwise return default based on data type
  if (attribute.dataType === AttributeTypeEnum.PlainText) {
    return EmptyString;
  } else if (attribute.dataType === AttributeTypeEnum.Reference) {
    return EmptyArray;
  }
  return null;
};

export const getDefaultValues = (
  fieldsMap: Record<string, Field>,
  attributeDefinitions?: Record<string, ObjectAttributeDefinition>
) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Object.keys(fieldsMap).reduce<Record<string, any>>((acc, field) => {
    const attribute = attributeDefinitions?.[field];
    if (attribute) {
      // Special logic for is_governing_document_required__a
      if (field === FieldEnum.IS_GOVERNING_DOCUMENT_REQUIRED) {
        const docSubType = acc[FieldEnum.DOCUMENT_SUB_TYPE];
        const docType = acc[FieldEnum.DOCUMENT_TYPE];
        let isRequired = false;
        if (
          docSubType &&
          typeof docSubType === 'object' &&
          'isGoverningDocumentRequired' in docSubType
        ) {
          isRequired = !!docSubType.isGoverningDocumentRequired;
        } else if (
          docType &&
          typeof docType === 'object' &&
          'isGoverningDocumentRequired' in docType
        ) {
          isRequired = !!docType.isGoverningDocumentRequired;
        }

        return {
          ...acc,
          [field]: isRequired,
        };
      }

      // Special logic for governing_document_id__a - use backend value or default to null
      if (field === FieldEnum.GOVERNING_DOCUMENT_ID) {
        const defaultValue = getDefaultValuesByAttributeDataType(attribute);
        return {
          ...acc,
          [field]: defaultValue ?? null,
        };
      }
      return {
        ...acc,
        [field]: getDefaultValuesByAttributeDataType(attribute),
      };
    }
    return acc;
  }, {});

export const hasAction = (
  actions: ObjectActionDefinition[],
  actionType: ActionTypeEnum
) => {
  return actions.some((a) => a.actionType === actionType);
};
/**
 *
 * @param mergedSections
 * @param attributeDefinitions
 * @returns new mergedSections with isRequired set to false if the field is hidden from the attributeDefinition
 */
export const overrideIsRequired = (
  mergedSections: { fields: Field[] },
  attributeDefinitions?: Record<string, ObjectAttributeDefinition>
) => {
  for (const field of mergedSections.fields) {
    // Special handling for document_sub_type__a
    if (field.columnName === FieldEnum.DOCUMENT_SUB_TYPE) {
      // Override the attribute definition to make it editable
      // This allows the layout rules to control editability based on conditions

      field.fieldFilterConditions = {
        filters: [
          {
            attribute: DocumentSubTypeObjectAttributeNameEnum.DOCUMENT_TYPE,
            operator: OperatorsEnum.EQUALS,
            value: `${ControlledDocumentObjectAttributeNameEnum.DOCUMENT_TYPE}`,
            isValueDynamic: true,
          },
        ],
        validationsWhenResponsePayloadIsEmpty: {
          isVisible: false,
          isEditable: false,
          isRequired: false,
        },
        validationsWhenResponsePayloadIsNotEmpty: {
          isVisible: true,
          isEditable: true,
          isRequired: true,
        },
      };

      field.layoutConfiguration = {
        jsonRuleCondition: LayoutRuleConditionEnum.VALUE_FROM_CONDITIONS,
        layoutConfig: {
          rules: [
            {
              conditions: {
                all: [
                  {
                    fact: `${ControlledDocumentObjectAttributeNameEnum.DOCUMENT_TYPE}`,
                    path: DocumentTypeObjectColumnNameEnum.DOES_NOT_HAVE_SUBTYPES,
                    operator: OperatorsEnum.EQUALS,
                    value: false,
                  },
                ],
              },
              event: {
                params: {
                  isVisible: true,
                  isEditable: true,
                  isRequired: true,
                },
                type: '',
              },
            },
          ],
        },
        defaultLayoutRules: {
          isVisible: false,
          isEditable: false,
          isRequired: false,
        },
      };
    }

    if (field.layoutConfiguration?.defaultLayoutRules?.isRequired) {
      if (
        attributeDefinitions?.[field.columnName]?.isHidden ||
        attributeDefinitions?.[field.columnName]?.isEditable === false
      ) {
        field.layoutConfiguration.defaultLayoutRules.isRequired = false;
        if ('rules' in field.layoutConfiguration.layoutConfig) {
          for (const rule of field.layoutConfiguration.layoutConfig.rules ||
            []) {
            if (rule.event.params?.isRequired)
              rule.event.params.isRequired = false;
          }
        }
      }
    }
  }
  return mergedSections;
};
